import type { Collection } from '@/types/collection';

/**
 * Компактная строка выбора для истории, результата и серии.
 *
 * Подписи берутся из коллекции: пользователь выбирал «Кафе у гавани», а не
 * harbour-cafe, и в истории должен видеть то же самое. Коллекции может не
 * быть под рукой (каталог ещё грузится), а вариант мог исчезнуть в новой
 * версии коллекции — в обоих случаях остаётся id: он хуже читается, но
 * честнее, чем пропуск строки или выдуманная подпись.
 */
export function describeSelections(
  collection: Collection | undefined,
  groups: Record<string, string[]>,
): string {
  // Коллекция приходит с сервера: сводка не должна ронять весь экран, если
  // её описание оказалось неполным.
  const optionGroups = collection?.optionGroups ?? [];
  const order = new Map(optionGroups.map((group) => [group.id, group.order]));

  const parts = Object.entries(groups)
    // Порядок групп задаёт коллекция, а не порядок ключей в jsonb.
    .sort(([left], [right]) => (order.get(left) ?? Number.MAX_SAFE_INTEGER) - (order.get(right) ?? Number.MAX_SAFE_INTEGER))
    .flatMap(([groupId, optionIds]) =>
      (optionIds ?? []).map((optionId) => labelFor(optionGroups, groupId, optionId)),
    )
    .filter((label): label is string => Boolean(label));

  return parts.length > 0 ? parts.join(', ') : '—';
}

function labelFor(
  optionGroups: Collection['optionGroups'],
  groupId: string,
  optionId: string,
): string {
  const group = optionGroups.find((candidate) => candidate.id === groupId);

  return group?.options.find((option) => option.id === optionId)?.label ?? optionId;
}
